let initialized = false;
let scoreLeaderboardId = null;

const SCORE_LEADERBOARD = 'dead-loop-high-score';

export function reportWavedashProgress(loaded = 0, total = 1) {
  const sdk = getSdk();
  if (!sdk?.updateLoadProgressZeroToOne) return false;
  const progress = total > 0 ? Math.max(0, Math.min(1, loaded / total)) : 1;
  try {
    sdk.updateLoadProgressZeroToOne(progress);
    return true;
  } catch (error) {
    console.warn('[wavedash] load progress failed', error);
    return false;
  }
}

export function initWavedash() {
  const sdk = getSdk();
  if (!sdk || initialized) return false;
  try {
    if (!sdk.initialized) sdk.init({ debug: isLocalHost(), deferEvents: false });
    sdk.readyForEvents?.();
    sdk.requestStats?.();
    initialized = true;
    primeLeaderboards();
    return true;
  } catch (error) {
    console.warn('[wavedash] init failed', error);
    return false;
  }
}

export function toggleWavedashOverlay() {
  const sdk = getSdk();
  if (!sdk?.toggleOverlay) return false;
  try {
    sdk.toggleOverlay();
    return true;
  } catch (error) {
    console.warn('[wavedash] overlay failed', error);
    return false;
  }
}

export function recordWavedashKill(game, enemy = {}, source = {}) {
  const sdk = getSdk();
  if (!sdk) return false;
  try {
    incrementStat(sdk, 'KILLS_TOTAL', 1, false);
    setMaxStat(sdk, 'BEST_COMBO_KILLS', game.comboKills || 0, false);
    if (source.source === 'passive') incrementStat(sdk, 'PASSIVE_KILLS_TOTAL', 1, false);
    if (enemy.boss) {
      incrementStat(sdk, 'BOSSES_KILLED', 1, false);
      setAchievement(sdk, 'BOSS_BREAKER', true);
    }
    if ((game.kills || 0) >= 1) setAchievement(sdk, 'FIRST_BLOOD', false);
    if ((game.comboKills || 0) >= 10) setAchievement(sdk, 'TEN_CHAIN', false);
    if ((game.kills || 0) >= 100) setAchievement(sdk, 'CENTURION', false);
    sdk.storeStats?.();
    return true;
  } catch (error) {
    console.warn('[wavedash] kill stat failed', error);
    return false;
  }
}

export async function submitWavedashRun(game) {
  const sdk = getSdk();
  if (!sdk) return false;
  try {
    setMaxStat(sdk, 'BEST_SCORE', game.score || 0, false);
    setMaxStat(sdk, 'BEST_WAVE', game.wave || 1, false);
    incrementStat(sdk, 'RUNS_PLAYED', 1, false);
    if ((game.wave || 1) >= 5) setAchievement(sdk, 'WAVE_FIVE', false);
    if ((game.score || 0) >= 10000) setAchievement(sdk, 'SCORE_10K', false);
    sdk.storeStats?.();
    await submitLeaderboardScore(sdk, game.score || 0);
    return true;
  } catch (error) {
    console.warn('[wavedash] run submit failed', error);
    return false;
  }
}

function getSdk() {
  return globalThis.Wavedash || null;
}

function isLocalHost() {
  return ['localhost', '127.0.0.1', ''].includes(globalThis.location?.hostname || '');
}

async function primeLeaderboards() {
  const sdk = getSdk();
  if (!sdk?.getOrCreateLeaderboard) return;
  try {
    const response = await sdk.getOrCreateLeaderboard(
      SCORE_LEADERBOARD,
      sdk.LeaderboardSortOrder?.DESC ?? 1,
      sdk.LeaderboardDisplayType?.NUMERIC ?? 0,
    );
    scoreLeaderboardId = response?.success ? response.data?.id : null;
  } catch (error) {
    console.warn('[wavedash] leaderboard setup failed', error);
  }
}

async function submitLeaderboardScore(sdk, score) {
  if (!sdk?.uploadLeaderboardScore || score <= 0) return false;
  if (!scoreLeaderboardId) await primeLeaderboards();
  if (!scoreLeaderboardId) return false;
  const response = await sdk.uploadLeaderboardScore(scoreLeaderboardId, score, true);
  return !!response?.success;
}

function getStat(sdk, id) {
  return Number(
    sdk.getStat?.(id)
    ?? sdk.get_stat_int?.(id)
    ?? sdk.getStatInt?.(id)
    ?? 0
  ) || 0;
}

function setStat(sdk, id, value, storeNow) {
  if (sdk.setStat) return sdk.setStat(id, value, storeNow);
  if (sdk.set_stat_int) return sdk.set_stat_int(id, value, storeNow);
  if (sdk.setStatInt) return sdk.setStatInt(id, value, storeNow);
  return false;
}

function incrementStat(sdk, id, amount, storeNow) {
  return setStat(sdk, id, getStat(sdk, id) + amount, storeNow);
}

function setMaxStat(sdk, id, value, storeNow) {
  return setStat(sdk, id, Math.max(getStat(sdk, id), value), storeNow);
}

function setAchievement(sdk, id, storeNow) {
  if (sdk.setAchievement) return sdk.setAchievement(id, true, storeNow);
  if (sdk.set_achievement) return sdk.set_achievement(id, true, storeNow);
  return false;
}
